import { injectable } from 'inversify';
import React, { useCallback } from 'react';
import { LFC } from '../../../Legacy';
import { SliderWithInput } from '../../../ui/SliderWithInput';
import { resolve } from '../../Container';
import { Physical, RelativeTo } from '../../canvas/entities/Physical';
import { Physicals } from '../../canvas/entities/Physicals';
import { ParticipantManager } from '../../canvas/participants/ParticipantManager';
import { SpatialIndex } from '../../canvas/utilities/SpatialIndex';
import { Audited } from '../auditing/Audited';
import { FromFirstMatchExportAdapter } from '../dataExport/ExportAdapter';
import { ExportDimension, ExportDimensionUIProps } from '../dataExport/ExportDimension';
import { DefineColorColumn } from '../dataExport/addons/ColorDimension';
import { DefineDateColumn } from '../dataExport/addons/DateDimension';
import { DefineParticipantColumn } from '../dataExport/addons/ParticipantDimension';
import { DefineSimpleDimension, DefineSimpleDimensionWithUI } from '../dataExport/addons/SimpleDimension';
import { Sticky } from '../paper/Sticky';
import { Box } from './../../../ui';
import { Token } from './Token';

@injectable()
export class TokenExportAdapter extends FromFirstMatchExportAdapter {

    @resolve(ParticipantManager) private participants!: ParticipantManager;

    protected selector = c => c.is(Token);

    public readonly id = 'TokenExports';
    public readonly label = 'Tokens';
    public readonly priority = 10;
    public readonly defaults = true;
    public readonly columns = [
        DefineSimpleDimension(
            Token,
            'Content',
            'Adds a column that contains the text or value of each Token.',
            x => x.value
        ),
        DefineColorColumn(
            Token,
            'Color',
            'Adds a column that contains the color of each Token. You can choose from color name, color hex or RGBA format.',
            x => x.background
        ),
        DefineParticipantColumn(
            Token,
            'Author',
            'Adds a column that contains the creator of each Token.  You can choose name, email or both in the output.',
            x => this.participants.get(x.userId)
        ),
        DefineDateColumn(
            Token,
            'Created',
            'Adds a column that contains the date and time each Token was created.  Several date formatting options are available.',
            'shortDateTime',
            x => x.created
        ),
    ];

}

export const NearbyTokenColumnEditor: LFC<ExportDimensionUIProps> = ({ item, onEdit }) => {

    const onChange = useCallback((threshold: number) => onEdit(ExportDimension.update(item, { threshold })), [item, onEdit]);

    return <Box padding="1" width="24">
        <SliderWithInput
            unit="px"
            min={10}
            max={500}
            value={(item.params.threshold || 10) as any}
            onChange={onChange}
        />
    </Box>;
};

export class NearbyTokenExportAdapter extends FromFirstMatchExportAdapter {

    @resolve(SpatialIndex) private spatialIndex!: SpatialIndex;

    protected selector = c => c.is(Audited) && !c.is(Token);

    public readonly id = 'NearbyToken';
    public readonly label = 'Experimental';
    public readonly priority = 30;
    public readonly columns = [
        DefineSimpleDimensionWithUI(
            Sticky,
            'Nearby Token',
            'Adds a column that contains the value of the nearest Token to each exported object in the dataset.',
            NearbyTokenColumnEditor,
            (c, params) => {
                const p = c.as(Physical);
                if (!p) return null;

                const threshold = params?.threshold || Token.Size.w;

                // Area of search is bounds +  token size
                const area = p.bounds(RelativeTo.Model)
                    .inflate([threshold, threshold]);

                // Find all nearby tokens
                const tokens = Physicals.polarize(this.spatialIndex.lookup(area), Token)
                    .filter(x => x.bounds(RelativeTo.Model).intersects(area));

                // Find the nearest
                const result = Physicals.nearestTo(tokens, p);
                return result?.value ?? null;
            }
        ),
    ];


}
